import { useEffect, useState, useMemo } from "react"
import { Link } from "react-router-dom"
import { CheckCircle2, ArrowRight, BookOpen, GraduationCap, Users, ShieldCheck, Briefcase, FileCheck, Landmark, PieChart } from "lucide-react"
import { Button } from "../../../components/ui/button"
import { Helmet } from "react-helmet-async"

const campusCtx = require.context("../../../assets/TopHeroImages", false, /\.(jpg|jpeg|png|JPG|PNG)$/i)
const getImg = (s) => (typeof s === "string" ? s : s?.default ?? s)
const ISCOBA_HERO = campusCtx.keys().length > 2 ? getImg(campusCtx(campusCtx.keys()[2])) : null

export default function Iscoba() {
    const navLinks = [
        { id: "presentation", num: "01", label: "Présentation" },
        { id: "objectifs", num: "02", label: "Objectifs" },
        { id: "admission", num: "03", label: "Admission" },
        { id: "debouches", num: "04", label: "Débouchés" }
    ]

    return (
        <div className="min-h-screen bg-background font-sans text-foreground antialiased">
            <Helmet>
                <title>ISCOBA - Institut de Comptabilité, Banque et Assurance | UNG</title>
                <meta name="description" content="Découvrez l'ISCOBA à l'Université de Ngozi : formation d'excellence en comptabilité, banque et assurance. Brevet de Technicien Supérieur Professionnel (BTSP)." />
            </Helmet>

            {/* Creative Masthead */}
            <header className="relative min-h-[65vh] flex items-center bg-primary overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {ISCOBA_HERO && <img src={ISCOBA_HERO} alt="" className="w-full h-full object-cover opacity-30 mix-blend-luminosity scale-105" aria-hidden />}
                    <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-transparent" />
                </div>
                <div className="container relative z-10 py-20">
                    <div className="max-w-4xl">
                        <div className="inline-flex items-center gap-4 mb-8 translate-y-4 animate-in fade-in duration-700 fill-mode-forwards">
                            <span className="text-[10px] uppercase tracking-[0.3em] text-accent font-black">Institut Supérieur</span>
                            <div className="h-px w-12 bg-accent/50" />
                            <span className="text-[10px] text-primary-foreground/60 uppercase tracking-widest font-bold">ISCOBA · Université de Ngozi</span>
                        </div>
                        <h1 className="heading-1 mb-8 leading-[0.95] font-serif tracking-tight text-white translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-200">
                            <span className="block italic font-light text-accent/90">L'Expertise</span>
                            <span className="block text-white mt-2">Financière & Audit</span>
                        </h1>
                        <div className="border-l-4 border-accent pl-8 mb-12 max-w-2xl translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-500">
                            <p className="text-lg md:text-xl font-light leading-relaxed text-primary-foreground/90">
                                Répondre aux défis de l'internationalisation des métiers de la banque et de l'assurance avec une formation polyvalente et pratique.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-6 items-center translate-y-4 animate-in fade-in duration-1000 fill-mode-forwards delay-700">
                            <Button className="bg-accent text-accent-foreground px-10 py-7 text-xs font-black transition-all duration-300 border-b-4 border-black/20 rounded-xl hover:bg-white hover:text-primary">
                                REJOINDRE L'INSTITUT
                                <ArrowRight className="w-5 h-5 ml-3" />
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Stats Bar */}
            <div className="border-y border-border bg-secondary/10">
                <div className="container py-8">
                    <div className="flex flex-wrap items-center justify-between gap-y-8">
                        {[
                            { val: "BTSP", label: "Brevet Délivré", icon: GraduationCap },
                            { val: "Audit", label: "Spécialisation", icon: PieChart },
                            { val: "Banque", label: "Secteur Clé", icon: Landmark },
                            { val: "100%", label: "Professionnalisant", icon: FileCheck }
                        ].map((stat, i) => (
                            <div key={i} className="flex items-center gap-5 group">
                                <div className="w-12 h-12 rounded-2xl bg-white border border-border flex items-center justify-center group-hover:bg-primary group-hover:border-primary group-hover:text-white transition-all duration-500 shadow-sm group-hover:shadow-xl group-hover:shadow-primary/20">
                                    <stat.icon className="w-6 h-6 text-primary group-hover:text-white transition-colors" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-xl font-black text-foreground group-hover:text-primary transition-colors">{stat.val}</span>
                                    <span className="text-[9px] text-muted-foreground uppercase font-black tracking-[0.2em]">{stat.label}</span>
                                </div>
                                {i < 3 && <div className="hidden xl:block h-10 w-px bg-border/50 ml-12" />}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content Article */}
            <article className="container py-16 lg:py-24 max-w-6xl mx-auto">
                <div className="grid lg:grid-cols-[280px_1fr] gap-16 lg:gap-24">
                    {/* Sidebar Summary */}
                    <aside className="lg:sticky lg:top-24 lg:self-start hidden lg:block">
                        <nav className="space-y-1">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground mb-8 pb-3 border-b border-border/50">
                                Sommaire
                            </p>
                            {navLinks.map((link) => (
                                <a
                                    key={link.id}
                                    href={`#${link.id}`}
                                    className="group flex items-center gap-4 py-3 text-[11px] font-black uppercase tracking-widest text-muted-foreground hover:text-primary transition-all duration-300"
                                >
                                    <span className="w-6 h-6 rounded-full bg-secondary/50 flex items-center justify-center text-[9px] text-accent group-hover:bg-primary group-hover:text-white transition-all">
                                        {link.num}
                                    </span>
                                    {link.label}
                                </a>
                            ))}
                        </nav>

                        <div className="mt-16 p-8 bg-primary/5 border-t-2 border-accent rounded-br-3xl">
                            <h4 className="text-[10px] uppercase tracking-[0.3em] font-black mb-6 text-primary">Le Label ISCOBA</h4>
                            <ul className="space-y-4 text-[11px] font-bold text-muted-foreground/80 leading-relaxed">
                                <li className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 bg-accent rounded-full mt-1.5 flex-shrink-0" />
                                    <span>Pédagogie ancrée dans les réalités du marché</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 bg-accent rounded-full mt-1.5 flex-shrink-0" />
                                    <span>Gage de qualité académique et professionnelle</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <div className="w-1.5 h-1.5 bg-accent rounded-full mt-1.5 flex-shrink-0" />
                                    <span>Accompagnement vers la performance</span>
                                </li>
                            </ul>
                        </div>
                    </aside>

                    {/* Main Content Sections */}
                    <main className="space-y-24">
                        {/* Section 01: Présentation */}
                        <section id="presentation" className="scroll-mt-24">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="w-10 h-[2px] bg-accent" />
                                <h2 className="text-sm uppercase tracking-[0.3em] font-black text-primary">01. Présentation</h2>
                            </div>
                            <div className="space-y-8">
                                <h3 className="text-3xl md:text-4xl font-serif font-black italic text-foreground leading-[1.15]">
                                    Une formation polyvalente pour <br /> les métiers du futur.
                                </h3>
                                <div className="prose-sm text-muted-foreground leading-relaxed space-y-5 max-w-3xl font-medium text-base">
                                    <p>
                                        L’Institut Supérieur de Comptabilité, Banque et Assurance (ISCOBA) est un établissement privé d’enseignement supérieur rattaché à l’Université de Ngozi.
                                    </p>
                                    <p>
                                        Les métiers liés à la comptabilité, aux finances, aux banques et aux assurances ont évolué ces dernières années par une internationalisation incontournable. L'ISCOBA répond à cette forte demande de profils polyvalents en élargissant les champs des connaissances et des savoir-faire de ses étudiants.
                                    </p>
                                    <p className="bg-secondary/5 p-6 border-l-4 border-primary italic">
                                        "Cette approche est facilitée par la qualité et la pertinence des enseignements qui visent un meilleur professionnalisme tout en préservant le caractère académique, gage de notre label de qualité."
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* Section 02: Objectifs */}
                        <section id="objectifs" className="scroll-mt-24">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="w-10 h-[2px] bg-accent" />
                                <h2 className="text-sm uppercase tracking-[0.3em] font-black text-primary">02. Objectifs</h2>
                            </div>

                            <div className="space-y-12">
                                <div className="max-w-3xl">
                                    <h4 className="text-sm font-black uppercase tracking-widest text-foreground mb-4">Objectif Général</h4>
                                    <p className="text-lg text-muted-foreground leading-relaxed border-l-2 border-border pl-6">
                                        Préparer l’étudiant au traitement pratique de tous les aspects juridiques, fiscaux, sociaux et comptables d’une entreprise : analyse de documents, rédaction de contrats et contentieux d’affaires.
                                    </p>
                                </div>

                                <div className="grid md:grid-cols-3 gap-8">
                                    {[
                                        { title: "Apprentissage Contextualisé", desc: "Dépasser les limites de l’approche magistrale pour des situations concrètes.", icon: BookOpen },
                                        { title: "Intégration Professionnelle", desc: "Favoriser l'insertion via des travaux pratiques intensifs et des stages.", icon: Users },
                                        { title: "Expertise de Haut Niveau", desc: "Former des agents financiers et bancaires prêts pour une entrée immédiate.", icon: ShieldCheck }
                                    ].map((obj, i) => (
                                        <div key={i} className="group p-8 bg-background border border-border hover:border-accent/30 rounded-3xl transition-all duration-500 hover:shadow-2xl hover:shadow-primary/5">
                                            <div className="w-12 h-12 bg-primary/5 rounded-2xl flex items-center justify-center text-primary group-hover:bg-accent group-hover:text-accent-foreground transition-all mb-8 shadow-sm">
                                                <obj.icon className="w-6 h-6" />
                                            </div>
                                            <h5 className="text-xs font-black uppercase tracking-[0.15em] text-foreground mb-4 leading-tight">{obj.title}</h5>
                                            <p className="text-[11px] text-muted-foreground leading-relaxed font-bold opacity-80">{obj.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>

                        {/* Section 03: Admission */}
                        <section id="admission" className="scroll-mt-24">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="w-10 h-[2px] bg-accent" />
                                <h2 className="text-sm uppercase tracking-[0.3em] font-black text-primary">03. Admission</h2>
                            </div>

                            <div className="grid lg:grid-cols-2 gap-12 items-center">
                                <div className="space-y-8">
                                    <p className="text-base text-muted-foreground leading-relaxed font-medium">
                                        Pour rejoindre le programme de l'ISCOBA, les candidats doivent remplir les critères académiques définis par l'Université de Ngozi.
                                    </p>
                                    <div className="grid gap-4">
                                        {[
                                            "Détenteur d'un diplôme de l'Enseignement Post-Fondamental",
                                            "Humanités Générales, Pédagogiques ou Techniques",
                                            "Participation obligatoire à l'Examen d'État",
                                            "Équivalence officielle pour les diplômes étrangers"
                                        ].map((item, i) => (
                                            <div key={i} className="flex items-start gap-4 p-4 bg-secondary/5 rounded-2xl border border-border/50 group hover:border-primary/20 transition-colors">
                                                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center mt-0.5 group-hover:bg-primary group-hover:text-white transition-all">
                                                    <CheckCircle2 className="w-3 h-3" />
                                                </div>
                                                <span className="text-[11px] font-black uppercase tracking-tight text-foreground/70">{item}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <div className="bg-primary/95 p-10 rounded-[2rem] text-white relative overflow-hidden shadow-2xl">
                                    <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-24 -mt-24" />
                                    <div className="relative space-y-6">
                                        <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center">
                                            <FileCheck className="w-6 h-6 text-accent" />
                                        </div>
                                        <h4 className="text-xl font-serif font-black italic">Cas particuliers</h4>
                                        <p className="text-sm font-medium leading-relaxed opacity-80">
                                            Les lauréats des promotions antérieures (avant 1999) peuvent être accueillis sur demande après passation d’un concours d’entrée organisé par l'Université.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Section 04: Débouchés */}
                        <section id="debouches" className="scroll-mt-24">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="w-10 h-[2px] bg-accent" />
                                <h2 className="text-sm uppercase tracking-[0.3em] font-black text-primary">04. Débouchés</h2>
                            </div>

                            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-16">
                                <div className="space-y-8">
                                    <div className="inline-flex items-center gap-3 px-4 py-2 bg-accent/10 border border-accent/20 rounded-full">
                                        <GraduationCap className="w-4 h-4 text-accent" />
                                        <span className="text-[10px] font-black uppercase tracking-widest text-accent">Diplôme d'État</span>
                                    </div>
                                    <h4 className="text-3xl font-serif font-black italic text-foreground leading-tight">
                                        Brevet de Technicien Supérieur Professionnel (BTSP)
                                    </h4>
                                    <p className="text-sm text-muted-foreground leading-relaxed font-medium">
                                        Ce diplôme permet au lauréat d’exercer comme professionnel ou indépendant auprès d’un large éventail d’organisations publiques et privées.
                                    </p>
                                </div>
                                <div className="space-y-3">
                                    {[
                                        "Banques & Institutions de Microfinance",
                                        "Compagnies d’Assurances",
                                        "Entreprises Publiques & Privées",
                                        "Groupements Coopératifs",
                                        "Indépendant en Fiscalité & Audit"
                                    ].map((career, i) => (
                                        <div key={i} className="flex items-center justify-between p-5 bg-secondary/5 border border-border/50 rounded-2xl hover:border-accent hover:bg-white transition-all group shadow-sm hover:shadow-xl hover:shadow-primary/5">
                                            <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground group-hover:text-primary transition-colors">{career}</span>
                                            <div className="w-8 h-8 rounded-full bg-transparent group-hover:bg-accent/10 flex items-center justify-center transition-all">
                                                <ArrowRight className="w-4 h-4 text-accent opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>
                    </main>
                </div>
            </article>

            {/* Premium CTA Footer */}
            <footer className="relative bg-primary py-24 overflow-hidden mt-12">
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-accent opacity-5 rounded-full blur-[120px] -mr-64 -mt-64" />
                    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white opacity-[0.02] rounded-full blur-[80px] -ml-32 -mb-32" />
                </div>

                <div className="container relative z-10 text-center max-w-4xl mx-auto">
                    <div className="inline-flex items-center gap-4 mb-10">
                        <div className="h-[1px] w-12 bg-accent/50" />
                        <span className="text-[10px] uppercase tracking-[0.5em] text-accent font-black">Future-Ready Skills</span>
                        <div className="h-[1px] w-12 bg-accent/50" />
                    </div>

                    <h2 className="text-4xl md:text-6xl font-serif font-black italic text-white mb-16 leading-[1.1]">
                        Bâtissez votre avenir <br /> dans la finance.
                    </h2>
                </div>
            </footer>
        </div>
    )
}
